import _ from 'lodash';
import { DateTime as LuxonDateTime, Settings } from 'luxon';
import dayjs from './dayjs/index.js';

const units = ['day', 'week', 'month', 'quarter', 'year', 'hour', 'minute', 'second'];
let nowMock;

export default class DateTime {
  static set defaultLocale(value) {
    dayjs.locale(value);
  }

  static set timezone(value) {
    Settings.defaultZoneName = value;
  }

  static get timezone() {
    return Settings.defaultZoneName;
  }

  static set fixedNow(value) {
    nowMock = value;
  }

  static get now() {
    if (nowMock) {
      return nowMock;
    }
    return new DateTime(LuxonDateTime.local().ts);
  }

  static fromISO(iso) {
    return new DateTime(LuxonDateTime.fromISO(iso).ts);
  }

  constructor(dt) {
    if (_.isNumber(dt)) {
      this._dt = new LuxonDateTime({ ts: dt });
    } else if (dt === undefined) {
      this._dt = DateTime.now._dt;
    } else if (_.isString(dt)) {
      this._dt = DateTime.fromISO(dt)._dt;
    } else if (dt instanceof Date) {
      this._dt = LuxonDateTime.fromJSDate(dt);
    } else {
      throw new Error('Invalid datetime value ' + dt);
    }
  }

  get ts() {
    return this._dt.ts;
  }

  get year() {
    return this._dt.year;
  }

  get month() {
    return this._dt.month;
  }

  get quarter() {
    return this._dt.quarter;
  }

  get day() {
    return this._dt.day;
  }

  get hour() {
    return this._dt.hour;
  }

  get minute() {
    return this._dt.minute;
  }

  get second() {
    return this._dt.second;
  }

  get millisecond() {
    return this._dt.millisecond;
  }

  get weekday() {
    return this._dt.plus({ days: 1 }).weekday;
  }

  get weekNumber() {
    return this._dt.plus({ days: 1 }).weekNumber;
  }

  toDate() {
    return this._dt.toJSDate();
  }

  toTime() {
    return this.toDate().getTime();
  }

  toISO() {
    return this._dt.toISO();
  }

  toJSON() {
    return this._dt.toJSON();
  }

  toString() {
    return this._dt.toString();
  }

  valueOf() {
    return this._dt.valueOf();
  }

  format(formatStr, locale) {
    const value = dayjs.utc(this._dt.ts).add(this._dt.offset, 'minute');

    return (locale ? value.locale(locale) : value).format(formatStr);
  }

  fromNow(withoutSuffix) {
    return dayjs(this._dt.ts).fromNow(withoutSuffix);
  }

  from(compared, withoutSuffix) {
    return dayjs(this._dt.ts).from(compared, withoutSuffix);
  }

  isEqual(date) {
    return this._dt.hasSame(date._dt, 'second');
  }

  add(number, unit) {
    return new DateTime(this._dt.plus({ [unit + 's']: number }).ts);
  }

  subtract(number, unit) {
    return new DateTime(this._dt.minus({ [unit + 's']: number }).ts);
  }

  startOf(unit) {
    if (unit === 'week') {
      return new DateTime(this._dt.plus({ days: 1 }).startOf(unit).ts).subtract(1, 'day');
    }
    return new DateTime(this._dt.startOf(unit).ts);
  }

  endOf(unit) {
    if (unit === 'week') {
      return new DateTime(this._dt.plus({ days: 1 }).endOf(unit).ts).subtract(1, 'day');
    }
    return new DateTime(this._dt.endOf(unit).ts);
  }

  set(values) {
    return new DateTime(this._dt.set(values).ts);
  }

  diff(date, unit, floating = false) {
    if (!units.includes(unit)) {
      throw new Error('Invalid unit ' + unit);
    }
    const other = date instanceof DateTime ? date : new DateTime(date);
    const duration = this._dt.diff(other._dt, unit + 's');

    if (floating) {
      return _.round(duration.values[unit + 's'], 1);
    } else {
      return _.floor(duration.values[unit + 's']);
    }
  }
}
